// Industry sub-pages data — paraphrased in-house content aligned to theme.

export const INDUSTRY_PAGES = {
  'ports-terminals': {
    slug: 'ports-terminals',
    num: '01',
    kicker: 'Ports & Terminals',
    title: 'AI-Powered Terminal Intelligence',
    subtitle:
      'Every container, every checkpoint, verified — the moment it crosses the gate.',
    hero:
      'Visotonics turns your terminal into a fully visible, fully verified operation. Deploy AI cameras across gates, yards and cranes to automatically track container identity, condition and location — in real time, with zero manual inspection.',
    pills: ['Gate & Yard', 'Crane & Lift', 'Damage Survey'],
    poster: 'https://images.pexels.com/photos/28185415/pexels-photo-28185415.jpeg',
    accent: 'from-cyan-400 to-sky-500',

    problem: {
      heading: 'Terminal operations are running on paper and guesswork',
      bullets: [
        'Damage surveys still rely on a surveyor with a clipboard and a phone camera',
        'Gate ID checks slow trucks to a stop-and-shoot crawl in every condition',
        'Yard location is tracked by radio call, not by record',
        'Crane lifts have no visual chain of custody when a dispute arises',
      ],
      result:
        'Disputed damage claims, stopped trucks, and hours lost every time a container can\u2019t be found or a dent can\u2019t be proven.',
    },

    solve:
      'Cameras deployed at the gate, over the yard, and on the crane capture every container as it moves \u2014 no stop, no scan, no surveyor required.',
    solveTag: 'Every gate. Every lift. Every container.',

    signals: [
      {
        title: 'Damage Detection',
        desc: 'Every dent, rust patch and crack detected and measured to mm\u00b2, automatically, from camera footage.',
      },
      {
        title: 'Identity & Seal Verification',
        desc: 'Container, ISO, trailer and wagon IDs read off moving trucks, with seal status checked on every pass.',
      },
      {
        title: 'Live Location',
        desc: 'A digital twin of the yard tracks every container\u2019s position automatically \u2014 no radio calls required.',
      },
      {
        title: 'Chain of Custody',
        desc: 'Every lift captured from multiple angles, building an auditable record from vessel to gate.',
      },
    ],

    figCaption: 'Checkpoint diff \u2014 gate in to gate out, damage attributed to the interval it occurred in.',
    figSpecs: [
      { l: 'SPECIMEN', v: '22G1' },
      { l: 'CHECKPOINT', v: 'GATE IN' },
      { l: 'FACES', v: '6 / 6 SCANNED' },
      { l: 'REPORT', v: '48 s' },
    ],

    worksFor: {
      heading: 'Any cargo. Any terminal. Any shift.',
      items: ['Container Freight', 'Bulk & Break-bulk', 'Tank Containers', 'Reefer Cargo', 'Ro-Ro', 'Project Cargo'],
      footer: 'Configurable per checkpoint, per terminal \u2014 works across gates, yards, cranes and CFS docks alike.',
    },

    intel: [
      {
        title: 'Terminal Insights',
        items: [
          'Real-time container count vs. manifest',
          'Checkpoint-wise damage attribution',
          'Shift-wise and gate-wise turnaround trends',
          'Delay and re-inspection flagging',
        ],
      },
      {
        title: 'Quality & Loss Prevention',
        items: [
          'Damage detection with mm\u00b2-level measurement',
          'Seal-mismatch and identity alerts',
          'Visual proof for damage disputes',
          'Survey and claims tracking',
        ],
      },
      {
        title: 'Deep Analysis',
        items: [
          'Turnaround benchmarking across shifts',
          'Root-cause view for gate bottlenecks',
          'Historical trend and anomaly reports',
          'Exportable, audit-ready logs',
        ],
      },
    ],

    footer:
      'We extract precise, verifiable identity, condition and location data from your terminal \u2014 so every container is accounted for, every time.',
  },

  'warehousing-distribution': {
    slug: 'warehousing-distribution',
    num: '02',
    kicker: 'Warehousing & Distribution',
    title: 'AI-Powered Warehouse Intelligence',
    subtitle: 'Every case counted, every pallet measured \u2014 from inbound to outbound.',
    hero:
      'Visotonics turns your warehouse floor into a fully visible, fully verified operation. Deploy AI cameras across docks and aisles to automatically track case count, dimensions, and damage \u2014 in real time, with zero manual tally sheets.',
    pills: ['Inbound & Outbound', 'Dimensioning', 'Damage & Audit'],
    poster: 'https://images.pexels.com/photos/34207359/pexels-photo-34207359.jpeg',
    accent: 'from-cyan-400 to-teal-400',

    problem: {
      heading: 'Warehouse floors are operating on tally sheets',
      bullets: [
        'Manual counts during stuffing and destuffing don\u2019t match the manifest',
        'Dimensioning is done by hand, or not at all',
        'Damage on arrival is often caught only after it becomes a customer dispute',
        'No video record exists when a carrier or customer disputes a shortage',
      ],
      result: 'Inventory shrinkage nobody can pin down, and disputes settled by whoever tells the better story \u2014 not the better record.',
    },

    solve:
      'Cameras deployed at the dock and along the aisle capture every carton, pallet and drum as it moves \u2014 no manual tally required.',
    solveTag: 'Every dock. Every aisle. Every case.',

    signals: [
      { title: 'Case Count', desc: 'Every carton, bag, pallet and drum tallied automatically during loading and unloading.' },
      { title: 'Volumetric Dimensioning', desc: 'Length, width and height captured from camera view \u2014 defensible data for billing and slotting.' },
      { title: 'Damage on Arrival', desc: 'Dents, tears and wet damage flagged the moment goods hit the dock.' },
      { title: 'Manifest Matching', desc: 'Delivery notes and packing lists converted to structured data and checked against what the cameras see.' },
    ],

    figCaption: 'Camera-based audit and dimensioning \u2014 inbound cargo measured automatically, not estimated.',
    figSpecs: [
      { l: 'DOCK', v: '02' },
      { l: 'MODE', v: 'CONTINUOUS' },
      { l: 'INDEX', v: 'BY ID' },
      { l: 'CLIP', v: 'SEARCHABLE' },
    ],

    worksFor: {
      heading: 'Any cargo. Any warehouse. Any shift.',
      items: ['FMCG & Packaged Goods', '3PL & Distribution', 'Cold Storage', 'E-commerce Fulfilment', 'Retail & Wholesale', 'Bulk Storage'],
      footer: 'Configurable per dock, per aisle \u2014 works on forklifts, conveyors and manual staging areas alike.',
    },

    intel: [
      {
        title: 'Warehouse Insights',
        items: ['Real-time case count vs. manifest', 'Dock-wise and aisle-wise throughput trends', 'Inbound vs. outbound reconciliation', 'Delay and bottleneck flagging'],
      },
      {
        title: 'Quality & Loss Prevention',
        items: ['Damage detection on arrival and dispatch', 'Shortage and miscount alerts', 'Visual proof for carrier disputes', 'Claims and rework tracking'],
      },
      {
        title: 'Deep Analysis',
        items: ['Productivity benchmarking across shifts', 'Root-cause view for shrinkage', 'Historical trend and anomaly reports', 'Exportable, audit-ready logs'],
      },
    ],

    footer:
      'We extract precise, verifiable count, dimension and condition data from your floor \u2014 so every case is accounted for, every time.',
  },

  'manufacturing': {
    slug: 'manufacturing',
    num: '03',
    kicker: 'Manufacturing',
    title: 'AI-Powered Production Intelligence',
    subtitle: 'Every item counted, classified and inspected \u2014 the moment it leaves the line.',
    hero:
      'Visotonics turns your production floor into a fully visible, fully verified operation. Deploy AI cameras across your line to automatically track production count, throughput, SKU-wise output, and external damage \u2014 in real time, with zero manual tally.',
    pills: ['Production Line', 'Quality Control', 'Packaging & Dispatch'],
    poster: 'https://images.pexels.com/photos/34207359/pexels-photo-34207359.jpeg',
    accent: 'from-cyan-400 to-emerald-400',

    problem: {
      heading: 'Production floors are operating blind',
      bullets: [
        'Manual production counts rely on end-of-line tally sheets and register entries',
        'No real-time visibility into throughput or line performance',
        'SKU mix-ups and mislabeled units go undetected',
        'External damage and packaging defects are often caught only at dispatch \u2014 or by the customer',
      ],
      result: 'Inaccurate production counts, hidden productivity loss, and quality issues that surface only after the product has left the line.',
    },

    solve:
      'Cameras deployed above the line, at the packing station, and at the exit conveyor capture every unit as it\u2019s produced \u2014 no manual tally required.',
    solveTag: 'Every line. Every shift. Every unit.',

    signals: [
      { title: 'Production Count', desc: 'Every unit tallied automatically as it passes the camera, per line and per shift.' },
      { title: 'SKU-wise Identification', desc: 'Each item classified by SKU/variant to keep production mix accurate in real time.' },
      { title: 'External Damage Detection', desc: 'Dents, tears, cracks and packaging defects flagged the instant they occur.' },
      { title: 'Throughput & Productivity', desc: 'Units-per-minute and line efficiency computed continuously, not at shift-end.' },
    ],

    figCaption: 'Line camera \u2014 continuous production and process monitoring, no separate inspection station.',
    figSpecs: [
      { l: 'MODE', v: 'ONE PASS' },
      { l: 'CAMERA', v: 'ONE UNIT' },
      { l: 'PIPELINE', v: 'COUNT \u00b7 ID \u00b7 INSPECT' },
      { l: 'FLOW', v: 'IN \u25b8\u25b8 DISPATCH' },
    ],

    worksFor: {
      heading: 'Any product. Any line. Any shift.',
      items: ['FMCG & Packaged Goods', 'Electronics & Components', 'Pharma & Healthcare', 'Textiles & Apparel', 'Automotive Parts', 'Food & Beverage'],
      footer: 'Configurable per SKU, per line \u2014 works on conveyors, tables, and manual packing stations alike.',
    },

    intel: [
      {
        title: 'Production Insights',
        items: ['Real-time production count vs. target', 'SKU-wise output breakdown', 'Shift-wise and line-wise throughput trends', 'Downtime and slow-cycle flagging'],
      },
      {
        title: 'Quality & Loss Prevention',
        items: ['External damage / defect detection', 'Mis-packed or wrong-SKU alerts', 'Visual proof for quality disputes', 'Rework and rejection tracking'],
      },
      {
        title: 'Deep Analysis',
        items: ['Productivity benchmarking across shifts', 'Root-cause view for bottlenecks', 'Historical trend and anomaly reports', 'Exportable, audit-ready logs'],
      },
    ],

    footer:
      'We extract precise, verifiable count, SKU and quality data from the line \u2014 so every unit produced is accounted for, every time.',
  },

  'logistics-supply-chain': {
    slug: 'logistics-supply-chain',
    num: '04',
    kicker: 'Logistics & Supply Chain',
    title: 'AI-Powered Supply Chain Intelligence',
    subtitle: 'One record, every handoff \u2014 from vessel to gate to dock to line.',
    hero:
      'Visotonics doesn\u2019t stop at one facility. Deploy AI cameras across every node your cargo passes through to automatically track identity, condition and custody \u2014 in real time, with zero disputed handoffs.',
    pills: ['Multi-node Visibility', 'Chain of Custody', 'Document Intelligence'],
    poster: 'https://images.pexels.com/photos/14068258/pexels-photo-14068258.jpeg',
    accent: 'from-cyan-400 to-indigo-400',

    problem: {
      heading: 'Supply chains lose the story at every handoff',
      bullets: [
        'Every handoff is a place where accountability gets fuzzy \u2014 was it damaged at gate-in, or on the crane?',
        'Each party runs its own systems, paperwork and version of events',
        'No single record follows cargo from vessel to final delivery',
        'Disputes get resolved by whoever has the better story, not the better record',
      ],
      result: 'Disputed claims, duplicated paperwork, and no way to prove exactly where or when something went wrong.',
    },

    solve:
      'Cameras already in place at every node \u2014 port, warehouse, factory \u2014 capture identity, condition and custody automatically, no new hardware required.',
    solveTag: 'Every node. Every handoff. One record.',

    signals: [
      { title: 'Checkpoint Diff', desc: 'Any two checkpoints, anywhere on the chain, compared for auditable attribution of exactly where damage or shortage occurred.' },
      { title: 'Tamper-Evident Logbook', desc: 'A time-stamped record per movement, from vessel to gate to warehouse to line.' },
      { title: 'Identity & Count in Motion', desc: 'IDs and item counts verified at every gate along the route, not just the first one.' },
      { title: 'Document Normalization', desc: 'Bills of lading, manifests and certificates from every party read and converted into one structured format.' },
    ],

    figCaption: 'Chain of custody \u2014 vessel to yard to gate, one continuous record.',
    figSpecs: [
      { l: 'LIFT', v: '0142' },
      { l: 'CAMERAS', v: 'MULTI-ANGLE' },
      { l: 'SEVERITY', v: 'HIGH \u00b7 0.87' },
      { l: 'STAMP', v: '14:02:11' },
    ],

    worksFor: {
      heading: 'Any node. Any party. Any handoff.',
      items: ['Ports & Terminals', 'Warehousing & Distribution', 'Manufacturing', '3PL & Freight Forwarding', 'Cold Chain', 'Last-Mile Delivery'],
      footer: 'Configurable across every node in the chain \u2014 works wherever a camera already watches the handoff.',
    },

    intel: [
      {
        title: 'Network Insights',
        items: ['Real-time visibility across every node', 'Checkpoint-wise attribution of damage or shortage', 'Node-wise and party-wise turnaround trends', 'Delay and dispute flagging'],
      },
      {
        title: 'Quality & Loss Prevention',
        items: ['Cross-node damage and shortage attribution', 'Identity and seal-mismatch alerts', 'Visual proof for multi-party disputes', 'Claims tracking across the full chain'],
      },
      {
        title: 'Deep Analysis',
        items: ['Network-wide performance benchmarking', 'Root-cause view for recurring bottlenecks', 'Historical trend and anomaly reports', 'Exportable, audit-ready logs, API-first'],
      },
    ],

    footer:
      'We extract precise, verifiable identity, custody and condition data across your entire network \u2014 so every handoff is accounted for, every time.',
  },
};

export const INDUSTRY_LIST = Object.values(INDUSTRY_PAGES);
